/**
 * EventModal.js — Create/Edit Event modal
 *
 * Used in two contexts:
 *   1. AdminEventsPage — full form (isEdit=false for new, isEdit=true for edit)
 *   2. AdminPerformersPage — simplified form when booking a performer (prefilledPerformer set)
 *
 * The prefilledPerformer prop controls a "simplified" mode:
 *   - Event Name field is hidden; the name is auto-generated at submit time from
 *     the selected type and date (e.g. "SCALA Showcase – 16 Jun 2026")
 *   - Venue and MC fields are hidden (assigned later at event level)
 *   - Lyric detail fields are hidden (set by default from lyrics_sets_count)
 *
 * The parent is responsible for the actual API call — this component only
 * manages form state and calls onSave(form) with the current form values.
 */

import React, { useState } from 'react';

const toDateStr = (d) => {
  if (!d) return '';
  const s = typeof d === 'string' ? d : d.toISOString();
  return s.split('T')[0];
};

// Map event_type values to human-readable display labels for auto-generated names
const TYPE_LABELS = {
  showcase:          'SCALA Showcase',
  peoples_choice:    "SCALA People's Choice",
  lyricists:         '3 Lyricists',
  songwriters_round: 'Songwriters in the Round',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Generate a human-readable event name from type + date.
 * Used when creating an event from the Book button (no name field shown).
 * Example: "SCALA Showcase – 16 Jun 2026"
 *
 * @param {string} eventType  - e.g. 'showcase'
 * @param {string} eventDate  - YYYY-MM-DD string
 * @returns {string}
 */
function autoEventName(eventType, eventDate) {
  const label = TYPE_LABELS[eventType] || 'Event';
  if (!eventDate) return label;
  const [year, month, day] = eventDate.split('-');
  return `${label} – ${parseInt(day)} ${MONTHS[parseInt(month) - 1]} ${year}`;
}

/**
 * @param {Object}  props
 * @param {boolean} props.isEdit              - true = edit mode, false = create mode
 * @param {Object}  [props.event]             - Existing event object (edit mode)
 * @param {Array}   props.venues              - Available venues for the dropdown
 * @param {Array}   props.mcs                 - Available MCs for the dropdown
 * @param {Function} props.onSave             - Called with form values on submit
 * @param {Function} props.onClose            - Called when modal should close
 * @param {boolean} props.saving              - Disables save button while request is in-flight
 * @param {string}  [props.error]             - Error message to display inside the modal
 * @param {Object}  [props.prefilledPerformer] - When set, enables simplified booking mode
 */
export default function EventModal({ isEdit, event, venues, mcs, onSave, onClose, saving, error, prefilledPerformer }) {
  const [form, setForm] = useState({
    name:              event?.name || '',
    event_type:        event?.event_type || '',
    event_date:        event ? toDateStr(event.event_date) : '',
    venue_id:          event?.venue_id ? String(event.venue_id) : '',
    mc_id:             event?.mc_id ? String(event.mc_id) : '',
    notes:             event?.notes || '',
    lyricists_count:   event?.lyricists_count ?? 3,
    lyrics_sets_count: event?.lyrics_sets_count ?? 3,
    status:            event?.status || 'planning',
    lyric_details:     (event?.lyric_sets || []).map(ls => ({ name: ls.title || '', writer: ls.writer || '' })),
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const lyricsCount = parseInt(form.lyrics_sets_count) || 0;
  const displayedLyricDetails = Array.from({ length: lyricsCount }, (_, i) =>
    form.lyric_details[i] || { name: '', writer: '' }
  );
  const updateLyricDetail = (idx, field, value) => {
    const details = Array.from({ length: lyricsCount }, (_, i) =>
      form.lyric_details[i] || { name: '', writer: '' }
    );
    details[idx] = { ...details[idx], [field]: value };
    set('lyric_details', details);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In simplified (booking) mode, auto-generate the name so the caller
    // receives a complete form object without needing to know about this mode
    const submitForm = prefilledPerformer
      ? { ...form, name: autoEventName(form.event_type, form.event_date) }
      : form;
    onSave(submitForm);
  };

  // True when opened from the Book button — drives field visibility
  const simplified = !!prefilledPerformer;

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '520px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
            {isEdit ? 'Edit Event' : 'Create Event'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}
          >×</button>
        </div>
        {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          {/* Event Name: hidden in simplified mode — auto-generated at submit */}
          {!simplified && (
            <div className="a-field">
              <label className="a-label">Event Name *</label>
              <input className="a-input" type="text" value={form.name}
                onChange={e => set('name', e.target.value)} required />
            </div>
          )}
          <div className="a-field">
            <label className="a-label">Event Type *</label>
            <select className="a-select" value={form.event_type}
              onChange={e => set('event_type', e.target.value)} required>
              <option value="" disabled>Select a type…</option>
              <option value="showcase">SCALA Showcase</option>
              <option value="peoples_choice">SCALA People's Choice</option>
              <option value="lyricists">3 Lyricists Walk Into a Bar</option>
              <option value="songwriters_round">Songwriters in the Round</option>
            </select>
          </div>
          <div className="a-field">
            <label className="a-label">Event Date *</label>
            <input className="a-input" type="date" value={form.event_date}
              onChange={e => set('event_date', e.target.value)} required />
          </div>
          {/* Venue: hidden in simplified mode — assigned later at event level */}
          {!simplified && (
            <div className="a-field">
              <label className="a-label">Venue</label>
              <select className="a-select" value={form.venue_id}
                onChange={e => set('venue_id', e.target.value)}>
                <option value="">Select a venue...</option>
                {venues.map(v => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
              </select>
            </div>
          )}
          {/* MC: hidden in simplified mode — assigned later at event level */}
          {!simplified && (
            <div className="a-field">
              <label className="a-label">MC</label>
              <select className="a-select" value={form.mc_id}
                onChange={e => set('mc_id', e.target.value)}>
                <option value="">None</option>
                {mcs.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
              </select>
            </div>
          )}
          <div className="a-field">
            <label className="a-label">Notes</label>
            <textarea className="a-input" rows={3} value={form.notes}
              onChange={e => set('notes', e.target.value)}
              style={{ resize: 'vertical' }} />
          </div>
          {/* Set/lyric count: lyricists shows "Number of Lyrics", songwriters_round shows "Number of Sets" */}
          {(form.event_type === 'lyricists' || form.event_type === 'songwriters_round') && !simplified && (
            <div className="a-field">
              <label className="a-label">
                {form.event_type === 'songwriters_round' ? 'Number of Sets' : 'Number of Lyrics'}
              </label>
              <input className="a-input" type="number" min={1} max={10}
                value={form.lyrics_sets_count}
                onChange={e => set('lyrics_sets_count', parseInt(e.target.value) || 3)} />
            </div>
          )}
          {/* Lyric name/writer fields: lyricists only, not songwriters_round */}
          {form.event_type === 'lyricists' && !simplified && (
            <>
              {displayedLyricDetails.map((ld, i) => (
                <div key={i} style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--navy)', marginBottom: '0.375rem' }}>
                    Lyric {i + 1}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div className="a-field" style={{ marginBottom: 0 }}>
                      <label className="a-label">Lyric Name</label>
                      <input className="a-input" type="text"
                        value={ld.name}
                        onChange={e => updateLyricDetail(i, 'name', e.target.value)}
                        placeholder="e.g. The River" />
                    </div>
                    <div className="a-field" style={{ marginBottom: 0 }}>
                      <label className="a-label">Writer</label>
                      <input className="a-input" type="text"
                        value={ld.writer}
                        onChange={e => updateLyricDetail(i, 'writer', e.target.value)}
                        placeholder="e.g. Jane Smith" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {isEdit && (
            <div className="a-field">
              <label className="a-label">Status</label>
              <select className="a-select" value={form.status}
                onChange={e => set('status', e.target.value)}>
                <option value="planning">Planning</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving}>
              {saving ? 'Saving…' : 'Save Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
